import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Phone, MessageCircle, Bed, Utensils, Waves, Trees, Wind, Music, Film, Flame } from 'lucide-react';
import { villaData } from '../data/mockData';

const iconMap = {
  Bed, Utensils, Waves, Trees, Wind, Music, Film, Flame
};

export const HomePage = () => {
  const handleWhatsApp = () => {
    window.open(`https://wa.me/${villaData.whatsapp.replace(/[^0-9]/g, '')}?text=Salam, villa haqqında məlumat almaq istəyirəm`, '_blank');
  };
  
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 lg:pt-32 lg:pb-24 bg-gradient-to-br from-green-50 via-blue-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div className="space-y-6">
              <div className="inline-block px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                Quba'da İdeal İstirahət Yeri
              </div>
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 leading-tight">
                {villaData.name}
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                {villaData.description}
              </p>
              
              {/* Pricing Info */}
              <div className="flex items-center space-x-4 py-4">
                <div className="bg-white px-6 py-3 rounded-xl shadow-sm border border-green-100">
                  <p className="text-sm text-gray-600">Həftə içi</p>
                  <p className="text-2xl font-bold text-green-700">{villaData.pricing.weekday} ₼</p>
                  <p className="text-xs text-gray-500">gecəlik</p>
                </div>
                <div className="bg-white px-6 py-3 rounded-xl shadow-sm border border-blue-100">
                  <p className="text-sm text-gray-600">Həftə sonu</p>
                  <p className="text-2xl font-bold text-blue-700">{villaData.pricing.weekend} ₼</p>
                  <p className="text-xs text-gray-500">gecəlik</p>
                </div>
              </div>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <a href={`tel:${villaData.phone}`}>
                  <Button size="lg" className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg">
                    <Phone className="w-5 h-5 mr-2" />
                    Zəng Et
                  </Button>
                </a>
                <Button
                  size="lg"
                  onClick={handleWhatsApp}
                  className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp
                </Button>
              </div>
            </div>
            
            {/* Hero Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={villaData.photos[0]}
                  alt="Quba Nügədi Villa"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg">
                  <p className="text-sm font-semibold text-green-700">Mövcuddur</p>
                </div>
              </div>
              {/* Floating Cards */}
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-lg border border-gray-100">
                <p className="text-sm text-gray-600">Qonaq sayı</p>
                <p className="text-2xl font-bold text-gray-900">8-10 nəfər</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Əsas İmkanlar
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Rahat və xatirələnəsi istirahət üçün hər şey
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {villaData.features.map((feature, index) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <Card key={index} className="border-2 border-gray-100 hover:border-green-200 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-blue-100 rounded-xl flex items-center justify-center mb-4">
                      <IconComponent className="w-6 h-6 text-green-700" />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-sm text-gray-600">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
      
      {/* Highlights Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="grid grid-cols-2 gap-4">
              <img
                src={villaData.photos[1]}
                alt="Villa exterior"
                className="rounded-xl shadow-lg h-64 object-cover"
              />
              <img
                src={villaData.photos[2]}
                alt="Villa pool"
                className="rounded-xl shadow-lg h-64 object-cover mt-8"
              />
            </div>
            
            <div className="space-y-6">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                Niyə Bizim Villa?
              </h2>
              <div className="space-y-4">
                {villaData.highlights.map((highlight, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-sm">✓</span>
                    </div>
                    <p className="text-lg text-gray-700">{highlight}</p>
                  </div>
                ))}
              </div>
              <Link to="/about">
                <Button className="bg-green-600 hover:bg-green-700 text-white mt-4">
                  Daha Ətraflı
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-green-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Rezervasiya Üçün İndi Əlaqə Saxlayın
          </h2>
          <p className="text-xl text-green-50 mb-8">
            2 gecədən artıq qalmaq üçün xüsusi endirim!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${villaData.phone}`}>
              <Button size="lg" className="w-full sm:w-auto bg-white text-green-700 hover:bg-gray-100 px-8 py-6 text-lg">
                <Phone className="w-5 h-5 mr-2" />
                {villaData.phone}
              </Button>
            </a>
            <Button
              size="lg"
              onClick={handleWhatsApp}
              className="w-full sm:w-auto bg-green-800 hover:bg-green-900 text-white px-8 py-6 text-lg"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              WhatsApp Yaz
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};